import { useEffect, useRef, useState } from "react";
import { Facebook, Instagram, Youtube, ArrowUpRight } from "lucide-react";
import { useSettings } from "@/lib/SettingsContext";

function TikTokIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.5 3c.3 2.1 1.6 3.7 3.7 4v2.6c-1.3.1-2.6-.3-3.7-1v6.3c0 3.2-2.6 5.8-5.8 5.8S4.9 18.1 4.9 14.9s2.6-5.8 5.8-5.8c.3 0 .6 0 .9.1v2.7c-.3-.1-.6-.2-.9-.2-1.7 0-3.1 1.4-3.1 3.1s1.4 3.1 3.1 3.1 3.1-1.4 3.1-3.1V3h2.7z" />
    </svg>
  );
}

const NETS = [
  { key: "facebook", label: "Facebook", Icon: Facebook, brand: "#1877F2", fallbackName: "Dorothée Medium" },
  { key: "instagram", label: "Instagram", Icon: Instagram, brand: "#E1306C", fallbackName: "@dorotheelancelot" },
  { key: "tiktok", label: "TikTok", Icon: TikTokIcon, brand: "#25F4EE", fallbackName: "" },
  { key: "youtube", label: "YouTube", Icon: Youtube, brand: "#FF0000", fallbackName: "" },
];


const OFFICIAL_FACEBOOK = "https://www.facebook.com/DorotheeMedium.fr/";

function OfficialFacebookSection() {
  const [showPosts, setShowPosts] = useState(false);
  const [width, setWidth] = useState(300);
  const frameBox = useRef(null);
  useEffect(() => {
    const element = frameBox.current;
    if (!element) return;
    const update = () => setWidth(Math.max(180, Math.min(500, Math.floor(element.getBoundingClientRect().width))));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const pluginUrl = "https://www.facebook.com/plugins/page.php?" + new URLSearchParams({
    href: OFFICIAL_FACEBOOK, tabs: "timeline", width: String(width), height: "520",
    small_header: "true", adapt_container_width: "true", hide_cover: "false", show_facepile: "false"
  }).toString();
  return (
    <section aria-label="Ma page Facebook professionnelle officielle" className="mb-10 overflow-hidden rounded-2xl border border-[#D4B978]/35" style={{ background: "radial-gradient(ellipse at top left, rgba(212,185,120,.12), transparent 65%), #080b14" }}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-5 sm:p-8 md:p-10">
        <div className="min-w-0 flex flex-col justify-center items-start">
          <div className="dl-eyebrow mb-5 flex items-center gap-3"><Facebook size={20} aria-hidden="true" /> Ma page professionnelle officielle</div>
          <h3 className="serif text-3xl sm:text-4xl text-[#F3E9D2] leading-tight">Prolongeons la rencontre</h3>
          <p className="text-[#F3E9D2]/85 mt-4 leading-relaxed">Dorothée Lancelot Médium/Conférencière et Auteure</p>
          <p className="serif text-3xl sm:text-4xl text-[#D4B978] mt-7">Plus de 23 600 abonnés</p>
          <p className="text-[#F3E9D2]/55 text-xs mt-2">Chiffre indicatif, actualisé manuellement.</p>
          <p className="text-[#F3E9D2]/70 leading-relaxed mt-6 max-w-md">Mes réflexions, mes actualités, mes livres et nos échanges autour de la médiumnité et de mon univers. Retrouvez-moi sur ma page Dorothée Medium.</p>
          <a href={OFFICIAL_FACEBOOK} target="_blank" rel="noopener noreferrer" className="dl-btn dl-btn-solid mt-7 max-w-full text-center" style={{ whiteSpace: "normal" }}>Rejoignez-moi sur Facebook <ArrowUpRight size={16} aria-hidden="true" /></a>
        </div>
        <div className="min-w-0">
          <h4 className="serif text-2xl text-[#F3E9D2] mb-4">Les publications de ma page</h4>
          <div ref={frameBox} className="w-full min-w-0 overflow-hidden rounded-xl border border-[#D4B978]/20" style={{ minHeight: showPosts ? 520 : 240 }}>
            {showPosts ? <iframe key={width} title="Publications Facebook de Dorothée Medium — page professionnelle officielle" src={pluginUrl} width={width} height="520" loading="lazy" style={{ border: 0, display: "block", maxWidth: "100%", margin: "0 auto" }} allow="encrypted-media; picture-in-picture" /> :
              <div className="p-6 flex flex-col items-start justify-center" style={{ minHeight: 240 }}>
                <p className="text-[#F3E9D2]/70 text-sm leading-relaxed mb-5">Affichez les dernières publications directement ici. Ce contenu est fourni par Facebook et peut utiliser ses cookies.</p>
                <button type="button" className="dl-btn max-w-full" style={{ whiteSpace: "normal" }} onClick={() => setShowPosts(true)}>Afficher les publications Facebook</button>
              </div>}
          </div>
          <p className="text-[#F3E9D2]/55 text-xs leading-relaxed mt-4">Si Facebook ne permet pas l’affichage dans votre navigateur, <a className="dl-link" href={OFFICIAL_FACEBOOK} target="_blank" rel="noopener noreferrer">lisez mes publications sur ma page</a>.</p>
        </div>
      </div>
    </section>
  );
}


function NewsletterSection() {
  useEffect(() => {
    if (document.querySelector('script[data-dorothee-sender]')) return;
    const queue = window.sender || function () { (queue.q = queue.q || []).push(arguments); };
    window.Sender = 'sender';
    window.sender = queue;
    queue.l = queue.l || Date.now();
    queue.on = queue.on || function (event, callback) { queue.listeners = queue.listeners || {}; (queue.listeners[event] = queue.listeners[event] || []).push(callback); };
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://cdn.sender.net/accounts_resources/universal.js';
    script.setAttribute('data-dorothee-sender', 'true');
    document.head.appendChild(script);
    queue('1b660631bf2240');
  }, []);
  return (
    <section id="newsletter" aria-label="Les lettres de Dorothée" className="mb-10 rounded-2xl border border-[#D4B978]/35 p-5 sm:p-8 md:p-10" style={{ background: '#080b14' }}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <div className="min-w-0">
          <div className="dl-eyebrow mb-4">Une lettre, une rencontre</div>
          <h3 className="serif text-3xl sm:text-4xl text-[#F3E9D2]">Les lettres de Dorothée</h3>
          <p className="text-[#F3E9D2]/75 leading-relaxed mt-5">Deux fois par semaine, quelques mots pour partager mon univers. Choisissez vos rubriques : Oracle du Manoir, Chroniques de l’Étrange, Ésotérisme Book, médiumnité et anecdotes.</p>
          <p className="text-[#F3E9D2]/65 leading-relaxed mt-4">Mes salons, conférences, dédicaces et sorties de livres accompagnent les lettres de tous les abonnés.</p>
          <p className="text-[#F3E9D2]/55 text-sm leading-relaxed mt-5">Inscription gratuite, confirmée par email. Vous pouvez vous désinscrire à tout moment.</p>
          <a className="dl-link inline-block mt-5" href="https://stats.sender.net/forms/bW636g/view" target="_blank" rel="noopener noreferrer">Ouvrir le formulaire d’inscription <ArrowUpRight size={14} className="inline" aria-hidden="true" /></a>
        </div>
        <div className="min-w-0 w-full" style={{ textAlign: 'left' }}>
          <div className="sender-form-field" data-sender-form-id="bW636g" />
        </div>
      </div>
    </section>
  );
}

export default function SocialSection({ compact }) {
  const settings = useSettings();
 const social={ 
facebook: {
...(settings?.social?.facebook || {}),
url: settings?.social?.facebook?.url || "https://www.facebook.com/DorotheeMedium.fr/",
name: settings?.social?.facebook?.name || "Dorothée Lancelot",
},
instagram: {
...(settings?.social?.instagram || {}),
url: settings?.social?.instagram?.url || "https://www.instagram.com/dorotheelancelot/",
name: settings?.social?.instagram?.name || "@dorotheelancelot",
},
tiktok: {
...(settings?.social?.tiktok || {}),
url: settings?.social?.tiktok?.url || "https://www.tiktok.com/@dorotheelancelot?_r=1&_t=ZG-99sbI81HYw5",
name: settings?.social?.tiktok?.name || "@dorotheelancelot",
},
youtube: {
...(settings?.social?.youtube || {}),
url: settings?.social?.youtube?.url || "https://youtube.com/@dorotheelancelot?si=P01YpxYdL1bL9RRK",
name: settings?.social?.youtube?.name || "Dorothée Lancelot",
},
};

  const ytUrl = social.youtube?.url;
  const ttUrl = social.tiktok?.url;
  const strip = [
    { key: "youtube", label: "YouTube" },
    { key: "tiktok", label: "TikTok" },
    { key: "instagram", label: "Instagram" },
    { key: "facebook", label: "Facebook" },
  ].filter((s) => social[s.key]?.url);

  return (
    <section className={compact ? "" : "dl-container py-20 md:py-24"} data-testid="social-section">
      {!compact && (
        <div className="mb-12">
          <div className="dl-eyebrow mb-3">Communauté</div>
          <h2 className="serif text-4xl md:text-5xl text-[#F3E9D2]">Retrouvez Dorothée Lancelot sur les réseaux</h2>
          <p className="text-[#F3E9D2]/65 mt-4 max-w-2xl">
            Retrouvez mes vidéos, mes conférences, mes réflexions et mes contenus autour de l'ésotérisme,
            du deuil, de l'histoire et de la médiumnité sur mes différents réseaux.
          </p>
        </div>
      )}

      {!compact && <OfficialFacebookSection />}
      {!compact && <NewsletterSection />}

     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
<a className="dl-card p-6" href="https://www.facebook.com/DorotheeMedium.fr/" target="_blank" rel="noreferrer">
<Facebook size={24} />
<div className="mt-4">Facebook</div>
</a>

<a className="dl-card p-6" href="https://www.instagram.com/dorotheelancelot/" target="_blank" rel="noreferrer">
<Instagram size={24} />
<div className="mt-4">Instagram</div>
</a>

<a className="dl-card p-6" href="https://www.tiktok.com/@dorotheelancelot?_r=1&_t=ZG-99sbI81HYw5" target="_blank" rel="noreferrer">
<TikTokIcon size={24} />
<div className="mt-4">TikTok</div>
</a>

<a className="dl-card p-6" href="https://youtube.com/@dorotheelancelot?si=P01YpxYdL1bL9RRK" target="_blank" rel="noreferrer">
<Youtube size={24} />
<div className="mt-4">YouTube</div>
</a>
</div> 
        

      {(ytUrl || ttUrl) && (
        <div className="flex flex-wrap gap-3 mt-8">
          {ytUrl && <a href={ytUrl} target="_blank" rel="noreferrer" data-testid="yt-channel-btn" className="dl-btn dl-btn-solid"><Youtube size={16} /> Voir ma chaîne YouTube</a>}
          {ttUrl && <a href={ttUrl} target="_blank" rel="noreferrer" data-testid="tt-follow-btn" className="dl-btn"><TikTokIcon size={15} /> Me suivre sur TikTok</a>}
        </div>
      )}

      {strip.length > 0 && (
        <p className="mt-6 text-sm text-[#F3E9D2]/45">
          Retrouvez-moi aussi sur{" "}
          {strip.map((s, i) => (
            <span key={s.key}>
              <a href={social[s.key].url} target="_blank" rel="noreferrer" className="dl-link">{s.label}</a>
              {i < strip.length - 1 ? " • " : ""}
            </span>
          ))}
        </p>
      )}
    </section>
  );
}
